"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAuth, setIsAuth] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      const auth = sessionStorage.getItem("standave_auth");
      setIsAuth(auth === "true");
    };
    checkAuth();
    window.addEventListener("storage", checkAuth);
    return () => window.removeEventListener("storage", checkAuth);
  }, [pathname]);

  const handleLogout = () => {
    sessionStorage.removeItem("standave_auth");
    sessionStorage.removeItem("standave_user");
    setIsAuth(false);
    setMenuOpen(false);
    router?.push("/homepage");
  };

  const navLinks = [
    { label: "Home", href: "/homepage" },
    { label: "Our Team", href: "/team" },
    { label: "About", href: "/about" },
    { label: "Blog", href: "/blog" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0C1B33]/95 backdrop-blur-xl border-b border-[rgba(201,168,76,0.12)] shadow-[0_4px_40px_rgba(0,0,0,0.4)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 group">
          <AppLogo
            size={36}
            text="StanDave"
            iconName="BuildingOffice2Icon"
            className="text-cream"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              className={`nav-link font-display text-[15px] font-600 ${
                pathname === link?.href ? "active" : ""
              }`}
            >
              {link?.label}
            </Link>
          ))}
          {isAuth && (
            <Link
              href="/dashboard"
              className={`nav-link font-display text-[15px] font-600 ${
                pathname === "/dashboard" ? "active" : ""
              }`}
            >
              Dashboard
            </Link>
          )}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          {isAuth ? (
            <>
              <Link
                href="/dashboard"
                className="text-cream/80 hover:text-cream text-[14px] font-display font-600 uppercase tracking-widest transition-colors px-4 py-2 border border-[rgba(201,168,76,0.2)] hover:border-gold/40 rounded-sm"
              >
                My Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-cream/70 hover:text-cream text-[14px] font-display font-600 uppercase tracking-widest transition-colors"
              >
                <Icon name="ArrowRightOnRectangleIcon" size={16} className="text-current" />
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link
                href="/login"
                className="text-cream/80 hover:text-cream text-[14px] font-display font-600 uppercase tracking-widest transition-colors px-4 py-2"
              >
                Sign In
              </Link>
              <Link
                href="/signup"
                className="btn-primary px-6 py-2.5 text-[14px] rounded-sm font-display font-700"
              >
                Get Started
              </Link>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden text-cream p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <Icon
            name={menuOpen ? "XMarkIcon" : "Bars3Icon"}
            size={24}
            className="text-cream"
          />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0C1B33]/98 backdrop-blur-xl border-t border-[rgba(201,168,76,0.12)] px-6 py-6 space-y-4">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              onClick={() => setMenuOpen(false)}
              className={`block font-display text-[14px] font-600 uppercase tracking-widest py-2 transition-colors ${
                pathname === link?.href
                  ? "text-gold" :"text-cream/80 hover:text-cream"
              }`}
            >
              {link?.label}
            </Link>
          ))}
          {isAuth && (
            <Link
              href="/dashboard"
              onClick={() => setMenuOpen(false)}
              className={`block font-display text-[14px] font-600 uppercase tracking-widest py-2 transition-colors ${
                pathname === "/dashboard" ? "text-gold" : "text-cream/80 hover:text-cream"
              }`}
            >
              Dashboard
            </Link>
          )}
          <div className="pt-2 space-y-3 border-t border-[rgba(201,168,76,0.12)]">
            {isAuth ? (
              <>
                <Link
                  href="/dashboard"
                  onClick={() => setMenuOpen(false)}
                  className="btn-primary block text-center px-6 py-3 text-sm rounded-sm"
                >
                  My Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-center text-cream/60 hover:text-cream text-sm font-display uppercase tracking-widest py-2 transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  href="/login"
                  onClick={() => setMenuOpen(false)}
                  className="block text-center text-cream/70 hover:text-cream text-sm font-display uppercase tracking-widest py-2 border border-[rgba(201,168,76,0.2)] rounded-sm transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  href="/signup"
                  onClick={() => setMenuOpen(false)}
                  className="btn-primary block text-center px-6 py-3 text-sm rounded-sm"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}